import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The full-screen empty state, lifted from the Project screen (markup lines
 * 586-591): an 88px brand-tinted medallion, an Oswald headline, a 460px-wide
 * body paragraph and a single CTA.
 *
 * Note the medallion border is the *strong* brand border (#F0A66A), not the
 * softer one used on the peach capture card — the two are a shade apart and
 * the design uses them in different places.
 *
 * The action is a slot rather than a baked-in button so the caller supplies a
 * `<Button>`; the source's CTA is the standard orange fill.
 */
export type EmptyStateProps = {
  icon: LucideIcon;
  /** Rendered uppercase by the display font — pass sentence case. */
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
};

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "mx-auto max-w-[620px] px-[30px] py-[72px] text-center",
        className,
      )}
    >
      <div className="mx-auto mb-6 flex size-[88px] items-center justify-center rounded-hero border-[1.5px] border-brand-border-strong bg-brand-tint text-brand">
        <Icon size={40} />
      </div>
      <div className="font-display text-[1.7rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
        {title}
      </div>
      {description !== undefined && (
        <p className="mx-auto mb-7 mt-[14px] max-w-[460px] text-[1rem] leading-[1.6] text-muted">
          {description}
        </p>
      )}
      {action}
    </div>
  );
}
